import React, { useState } from "react";
import { useBrandedDialog } from '../BrandedDialog';
import { Download } from "lucide-react";
import { jsPDF } from "jspdf";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
import { saveAs } from "file-saver";

// ─── Document Export Utilities ────────────────────────────────────
function exportToMarkdown(name: string, content: string) {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  saveAs(blob, name.endsWith('.md') ? name : `${name}.md`);
}

function exportToPdf(name: string, content: string) {
  const doc = new jsPDF();
  const title = name.replace(/\.\w+$/, '');
  doc.setFontSize(16);
  doc.text(title, 14, 20);
  doc.setFontSize(10);
  const lines = doc.splitTextToSize(content, 180);
  let y = 32;
  for (const line of lines) {
    if (y > 280) { doc.addPage(); y = 14; }
    doc.text(line, 14, y);
    y += 5;
  }
  doc.save(`${title}.pdf`);
}

function exportToWord(name: string, content: string) {
  const title = name.replace(/\.\w+$/, '');
  const paragraphs = content.split('\n').map((line: string) => {
    if (line.startsWith('# '))
      return new Paragraph({ text: line.slice(2), heading: HeadingLevel.HEADING_1 });
    if (line.startsWith('## '))
      return new Paragraph({ text: line.slice(3), heading: HeadingLevel.HEADING_2 });
    if (line.startsWith('### '))
      return new Paragraph({ text: line.slice(4), heading: HeadingLevel.HEADING_3 });
    return new Paragraph({ children: [new TextRun(line)] });
  });
  const wordDoc = new Document({ sections: [{ children: paragraphs }] });
  Packer.toBlob(wordDoc).then((blob: Blob) => saveAs(blob, `${title}.docx`));
}

// Moved to component-level to use hooks
function openGoogleDocUrl(name: string) {
  window.open('https://docs.google.com/document/create?title=' + encodeURIComponent(name.replace(/\.\w+$/, '')), '_blank');
}

export function ExportMenu({ name, content }: { name: string; content: string }) {
  const [open, setOpen] = useState(false);
  const { alert: brandedAlert } = useBrandedDialog();
  const exportToGoogleDocs = (n: string, c: string) => {
    navigator.clipboard.writeText(c).then(() => {
      void brandedAlert('Content copied to clipboard. Paste it into the new Google Doc that will open.');
    }).catch(() => {
      void brandedAlert('Failed to copy to clipboard. Please copy manually.');
    });
    openGoogleDocUrl(n);
  };
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          background: 'var(--surface3)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: '4px 10px',
          color: 'var(--text)', cursor: 'pointer', fontSize: 'var(--text-sm)',
        }}
      >
        <Download size={13} /> Export
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 4,
            background: 'var(--bg-card, #1a1a2e)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)', overflow: 'hidden', zIndex: 100,
            minWidth: 160, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          {[
            { label: 'Markdown (.md)', action: () => exportToMarkdown(name, content) },
            { label: 'PDF (.pdf)', action: () => exportToPdf(name, content) },
            { label: 'Word (.docx)', action: () => exportToWord(name, content) },
            { label: 'Google Docs', action: () => exportToGoogleDocs(name, content) },
          ].map((item) => (
            <button
              key={item.label}
              onClick={() => { item.action(); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 14px', background: 'none', border: 'none',
                color: 'var(--text)', cursor: 'pointer', fontSize: 'var(--text-sm)',
                borderBottom: '1px solid var(--border)',
              }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.background = 'var(--surface3)'; }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.background = 'none'; }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
